import { ExternalLink } from "lucide-react";
import { type AssetNetwork } from "@cpg/common";
import { CopyButton } from "@/components/copy-button";
import { explorerAddressUrl, explorerTxUrl } from "@/lib/explorers";
import { cn } from "@/lib/utils";

// Single rendering for every table cell that shows a wallet address or
// transaction hash: truncated monospace value, an "open in explorer" link
// when one is configured for `network` (falls back to plain text — see
// lib/explorers.ts's null-safe contract), and a copy button. Consolidates
// what used to be duplicated per-route (address-with-vault.tsx and the
// invoice-detail page's local `ExplorerLink` both did their own version of
// this). Mirrors apps/merchant/src/components/hash-link.tsx.

function truncateHash(value: string): string {
  return value.length <= 16 ? value : `${value.slice(0, 6)}…${value.slice(-4)}`;
}

export interface HashLinkProps {
  value: string | null;
  type: "address" | "tx";
  network: AssetNetwork;
  className?: string;
}

export function HashLink({ value, type, network, className }: HashLinkProps) {
  if (!value) {
    return <span className="text-muted-foreground">—</span>;
  }

  const url = type === "tx" ? explorerTxUrl(network, value) : explorerAddressUrl(network, value);
  const label = type === "tx" ? "transaction" : "address";

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          title={value}
          aria-label={`Open ${label} in explorer`}
          className="inline-flex items-center gap-1 font-mono text-muted-foreground hover:text-primary hover:underline"
        >
          {truncateHash(value)}
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      ) : (
        <span className="font-mono text-foreground" title={value}>
          {truncateHash(value)}
        </span>
      )}
      <CopyButton
        value={value}
        label={`Copy ${label}`}
        className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
      />
    </div>
  );
}
